import Vue from 'vue';
import VueRouter, { Route } from 'vue-router';

import store from '@/store';
import i18n from "@/i18n";
import Root from '@/App.vue';

import { AppUserData } from '@/types/AppUser';
import Authorization from '@/types/Authorization';
import Configuration from '@/types/Configuration';

import AccessDenied from '@/views/AccessDenied.vue';
import NotFound from '@/views/NotFound.vue';
import Error from '@/views/Error.vue';
import Clients from '@/views/Clients.vue';
import Home from '@/views/Home.vue';
import Login from '@/views/Login.vue';
import Operations from '@/views/Operations.vue';
import PerformedWork from '@/views/PerformedWork.vue';
import Projects from '@/views/Projects.vue';
import Roles from '@/views/Roles.vue';
import Tasks from '@/views/Tasks.vue';
import Users from '@/views/Users.vue';

import TermsOfService from '@/views/TermsOfService.vue';
import PrivacyPolicy from '@/views/PrivacyPolicy.vue';

Vue.use(VueRouter)

const { locale } = i18n;

const routes = [
    {
        path: '/',
        redirect: locale
    },
    {
        path: '/termsofservice',
        redirect: locale + '/termsofservice'
    },
    {
        path: '/privacypolicy',
        redirect: locale + '/privacypolicy'
    },
    {
        path: '/accessdenied',
        redirect: locale + '/accessdenied'
    },
    {
        path: '/notfound',
        redirect: locale + '/notfound'
    },
    {
        path: '/error/:errorId',
        redirect: locale + '/error/:errorId',
        props: true
    },
    {
        path: '*',
        redirect: locale + '/notfound'
    },
    {
        path: '/clients',
        redirect: locale + '/clients'
    },
    {
        path: '/login',
        name: 'Login',
        component: Login
    },
    {
        path: '/performedWork',
        redirect: locale + '/performedWork'
    },
    {
        path: '/projects',
        redirect: locale + '/projects'
    },
    {
        path: '/tasks',
        redirect: locale + '/tasks'
    },
    {
        path: '/administration/users',
        redirect: locale + '/administration/users'
    },
    {
        path: '/administration/operations',
        redirect: locale + '/administration/operations'
    },
    {
        path: '/administration/roles',
        redirect: locale + '/administration/roles'
    },
    {
        path: '/:locale',
        component: Root,
        beforeEnter: (to: Route, from: Route, next: Function) => {
            const locale = to.params.locale;
            if (!Configuration.SupportedLocales.includes(locale)) return next(i18n.locale);

            if (i18n.locale !== locale) {
                i18n.locale = locale;
            }

            const appUserJson = store.state.appUser;
            let appUser: AppUserData | null = null;
            if (appUserJson && appUserJson.length > 0) {
                appUser = JSON.parse(store.state.appUser as string);
            }

            if (to.matched.some(record => record.meta.requiresAuth)) {
                if (!appUser) {
                    return next({
                        path: '/login',
                        query: { returnUrl: to.fullPath }
                    });
                } else {
                    Vue.prototype.$appUser = appUser;
                    // check if user is authorized to acces the page
                    if ((to.meta.requiresAdmin && appUser.isAdministrator) || (!to.meta.authorize || Authorization.checkAccessRights(appUser, to.meta.authorize))) {
                        return next();
                    }
                    //Not authorized, route to Access Denied page
                    else {
                        return next({ path: '/accessdenied' });
                    }
                }
            } else {
                Vue.prototype.$appUser = appUser;
                return next();
            }
        },
        children: [
            {
                path: '',
                name: 'Home',
                component: Home,
                meta: {
                    requiresAuth: true
                }
            },
            {
                path: 'termsofservice',
                name: 'TermsOfService',
                component: TermsOfService
            },
            {
                path: 'privacypolicy',
                name: 'PrivacyPolicy',
                component: PrivacyPolicy
            },
            {
                path: 'accessdenied',
                name: 'AccessDenied',
                component: AccessDenied
            },
            {
                path: 'notfound',
                name: 'NotFound',
                component: NotFound
            },
            {
                path: 'error/:errorId',
                name: 'Error',
                component: Error,
                props: true
            },
            {
                path: 'clients',
                name: 'Clients',
                component: Clients,
                meta: {
                    requiresAuth: true,
                    requiresAdmin: true
                }
            },
            {
                path: 'performedWork',
                name: 'PerformedWork',
                component: PerformedWork,
                meta: {
                    requiresAuth: true
                }
            },
            {
                path: 'projects',
                name: 'Projects',
                component: Projects,
                meta: {
                    requiresAuth: true
                }
            },
            {
                path: 'tasks',
                name: 'Tasks',
                component: Tasks,
                props: true,
                meta: {
                    requiresAuth: true
                }
            },
            {
                path: 'administration/users',
                name: 'Users',
                component: Users,
                meta: {
                    requiresAuth: true,
                    requiresAdmin: true
                }
            },
            {
                path: 'administration/roles',
                name: 'Roles',
                component: Roles,
                meta: {
                    requiresAuth: true,
                    requiresAdmin: true
                }
            },
            {
                path: 'administration/operations',
                name: 'Operations',
                component: Operations,
                meta: {
                    requiresAuth: true,
                    requiresAdmin: true
                }
            }
        ]
    }
]

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes
})

export default router
